import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { convertText } from "@/utils/langConvert";
import { DOWNLOAD_PAGE_PATH } from "@/config/download";
import BirthInfoForm from "./BirthInfoForm";
import AiBaziReport, { type BaziReportPerson } from "@/pages/paipan/components/AiBaziReport";
import { buildBaziReportData, buildExperienceDisplaySnapshot, toPaipanExtras } from "@/utils/baziReportData";
import { buildBaziReportRequestKey, type AiRequestGate } from "@/utils/aiBaziReportRequest";
import { withBasePath } from "../routes";
import type { CardData, BirthFormData, GeneratedBaziResult, ReportState } from "../types";

function toReportPerson(g: GeneratedBaziResult): BaziReportPerson {
  return {
    name: g.profile.name || "",
    gender: g.profile.gender || "",
    birthTime: g.birthTime || "",
    lunarDate: g.paipanData?.lunarDate || "",
    trueSolarTime: g.paipanData?.trueSolarTime || "",
    birthPlace: g.profile.birthPlace || "",
    fourPillars: g.detailData?.fourPillars
      ? `${g.detailData.fourPillars.year.stem}${g.detailData.fourPillars.year.branch} ${g.detailData.fourPillars.month.stem}${g.detailData.fourPillars.month.branch} ${g.detailData.fourPillars.day.stem}${g.detailData.fourPillars.day.branch} ${g.detailData.fourPillars.hour.stem}${g.detailData.fourPillars.hour.branch}`
      : "",
  };
}

export function buildExperienceAiReportKey(
  person?: BaziReportPerson,
): string {
  return buildBaziReportRequestKey(person);
}

interface ReportPanelProps {
  data: CardData;
  isLight: boolean;
  isDarkTheme: boolean;
  reportState: ReportState;
  formData: BirthFormData;
  setFormData: React.Dispatch<React.SetStateAction<BirthFormData>>;
  formMessage: string;
  generatedResult: GeneratedBaziResult | null;
  openProfileSelector: () => void;
  aiRequestGate: AiRequestGate;
  onStartGenerate: () => void;
  onResetGenerate: () => void;
}

export default function ReportPanel({
  data,
  isLight,
  isDarkTheme,
  reportState,
  formData,
  setFormData,
  formMessage,
  generatedResult,
  openProfileSelector,
  aiRequestGate,
  onStartGenerate,
  onResetGenerate,
}: ReportPanelProps) {
  const messageRef = useRef<HTMLDivElement>(null);
  const [showHePanModal, setShowHePanModal] = useState(false);
  const { i18n } = useTranslation();
  const trd = (text: string) => convertText(text, i18n.language);

  useEffect(() => {
    if (formMessage && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [formMessage]);

  return (
    <div className="space-y-3 md:space-y-5">
      {/* Intro */}
      <div>
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3
            className={`text-sm md:text-lg font-bold ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {trd(data.title)}
          </h3>
          <button
            onClick={openProfileSelector}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] md:text-xs font-medium border transition-colors cursor-pointer ${
              isLight
                ? "border-[#4a4a4c] text-[#4a4a4c] hover:bg-[#4a4a4c] hover:text-white"
                : "border-gold-500/50 text-gold-400 hover:bg-gold-500/15"
            }`}
          >
            <i className="ri-user-search-line text-[10px] md:text-xs" />
            {trd("选择/切换命主")}
          </button>
        </div>
        <p
          className={`text-xs md:text-sm leading-relaxed ${
            isLight ? "text-[#6e6e73]" : "text-silver-400"
          }`}
        >
          {trd(data.desc)}
        </p>
      </div>

      {/* 命主信息输入表单 */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className={`w-6 h-6 rounded-md flex items-center justify-center ${
              isLight ? "bg-[#4a4a4c]" : "bg-gold-500/20"
            }`}
          >
            <i className="ri-user-line text-[10px] md:text-sm text-white" />
          </div>
          <span
            className={`text-xs md:text-sm font-medium ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {trd("命主信息")}
          </span>
        </div>
        <BirthInfoForm
          data={data}
          isLight={isLight}
          formData={formData}
          setFormData={setFormData}
        />
      </div>

      {/* Divider */}
      <div
        className={`h-px w-full ${
          isLight ? "bg-[#e5e5ea]" : "bg-white/10"
        }`}
      />

      {/* Action buttons */}
      <div className="pt-2 pb-3">
        {formMessage && (
          <div
            ref={messageRef}
            className={`mb-3 flex items-start gap-2 px-3 py-2 rounded-lg text-xs md:text-xs leading-relaxed ${
              formMessage.includes("已切换命主") || formMessage.includes("已保存到")
                ? isLight
                  ? "bg-green-50 text-green-700 border border-green-100"
                  : "bg-green-500/10 text-green-400 border border-green-500/20"
                : formMessage.includes("已存在") || formMessage.includes("不重复")
                  ? isLight
                    ? "bg-amber-50 text-amber-700 border border-amber-100"
                    : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  : isLight
                    ? "bg-red-50 text-red-600 border border-red-100"
                    : "bg-red-500/10 text-red-400 border border-red-500/20"
            }`}
          >
            <i className={`flex-shrink-0 mt-0.5 ${
              formMessage.includes("已切换命主") || formMessage.includes("已保存到")
                ? "ri-check-line"
                : formMessage.includes("已存在") || formMessage.includes("不重复")
                  ? "ri-information-line"
                  : "ri-error-warning-line"
            }`} />
            <span>{trd(formMessage)}</span>
          </div>
        )}
        {reportState === "done" ? (
          <button
            onClick={onResetGenerate}
            className={`w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer ${
              isLight
                ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]"
                : "bg-gold-500 text-cosmic-950 hover:bg-gold-400"
            }`}
          >
            {trd("重新排盘")}
            <i className="ri-refresh-line text-[10px] md:text-sm" />
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={onStartGenerate}
              disabled={reportState === "generating"}
              className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer ${
                isLight
                  ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c] disabled:opacity-50"
                  : "bg-gold-500 text-cosmic-950 hover:bg-gold-400 disabled:opacity-50"
              }`}
            >
              {reportState === "generating" ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-[10px] md:text-sm" />
                  {trd("生成中...")}
                </>
              ) : (
                <>
                  {trd("开始排盘")}
                  <i className="ri-arrow-right-line text-[10px] md:text-sm" />
                </>
              )}
            </button>
            <button
              onClick={() => setShowHePanModal(true)}
              className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer border ${
                isLight
                  ? "border-[#4a4a4c] text-[#4a4a4c] hover:bg-[#4a4a4c] hover:text-white"
                  : "border-gold-500 text-gold-400 hover:bg-gold-500/15"
              }`}
            >
              {trd("八字合盘")}
              <i className="ri-group-line text-[10px] md:text-sm" />
            </button>
          </div>
        )}
      </div>

      {/* Collapsible result sections — only shown after generation */}
      {reportState === "done" && (
        <div className="space-y-3 md:space-y-4">
          {/* AI 報告 */}
          <div>
            {generatedResult && (() => {
              const aiReportPerson = toReportPerson(generatedResult);
              const aiReportExtras = toPaipanExtras(generatedResult.paipanData);
              const displaySnapshot = generatedResult
                ? buildExperienceDisplaySnapshot(generatedResult.detailData)
                : undefined;
              const aiReportData = buildBaziReportData(
                aiReportPerson,
                generatedResult.detailData,
                aiReportExtras,
                displaySnapshot,
              );
              return (
                <AiBaziReport
                  key={buildExperienceAiReportKey(aiReportPerson)}
                  isDark={isDarkTheme}
                  person={aiReportPerson}
                  data={aiReportData}
                  requestGate={aiRequestGate}
                />
              );
            })()}
          </div>

          {/* 下载 APP 提示 */}
          <Link
            to={DOWNLOAD_PAGE_PATH}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border transition-colors cursor-pointer ${
              isLight
                ? "bg-[#f5f5f7] border-[#e5e5ea] hover:bg-[#eaeaea]"
                : "bg-cosmic-900/40 border-white/10 hover:bg-white/5"
            }`}
          >
            <i className={`ri-download-cloud-line text-sm ${isLight ? "text-[#4a4a4c]" : "text-gold-400"}`} />
            <span className={`text-[10px] font-medium ${isLight ? "text-[#1c1c1e]" : "text-white"}`}>
              {trd("下载 APP 解锁完整功能体验")}
            </span>
          </Link>
        </div>
      )}

      {/* 八字合盘弹窗 */}
      {showHePanModal && (
        <BaziHePanModal isLight={isLight} onClose={() => setShowHePanModal(false)} />
      )}
    </div>
  );
}

/* ─── 八字合盘弹窗 ─── */
function BaziHePanModal({ isLight, onClose }: { isLight: boolean; onClose: () => void }) {
  const ink = {
    card: isLight ? 'rgba(255,255,255,0.97)' : 'rgba(107,78,113,0.28)',
    border: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(183,154,197,0.25)',
    text: isLight ? '#2d2d2d' : '#E8E0E8',
    textSub: isLight ? 'rgba(28,28,30,0.55)' : 'rgba(232,224,232,0.55)',
    btnPrimary: isLight ? 'linear-gradient(135deg, #8A6A3E, #A08050)' : 'linear-gradient(135deg, #E8C39E, #D4A86A)',
    btnSecondary: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(183,154,197,0.15)',
  };
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const downloadPagePath = DOWNLOAD_PAGE_PATH;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[500] flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 16 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="w-full max-w-[340px] rounded-2xl p-6 flex flex-col items-center gap-4 relative"
        style={{ background: ink.card, border: `1px solid ${ink.border}` }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full active:scale-90 transition-transform"
          style={{ background: ink.btnSecondary }}
        >
          <i className="ri-close-line text-sm" style={{ color: ink.textSub }} />
        </button>

        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mt-1"
          style={{ background: isLight ? 'linear-gradient(135deg, #8A6A3E, #A08050)' : 'linear-gradient(135deg, #E8C39E, #D4A86A)' }}
        >
          <i className="ri-group-line text-2xl" style={{ color: '#fff' }} />
        </div>

        <div className="text-center">
          <p className="text-sm font-bold mb-1" style={{ color: ink.text, fontFamily: "'Noto Serif TC', serif" }}>
            八字合盘
          </p>
          <p className="text-xs leading-relaxed" style={{ color: ink.textSub }}>
            八字合盘功能仅在命理乾坤APP中提供<br />请前往APP体验完整合盘功能
          </p>
        </div>

        <button
          onClick={() => window.open(withBasePath(downloadPagePath), '_blank')}
          className="w-full py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
          style={{ background: ink.btnPrimary, color: '#fff', fontFamily: "'Noto Serif TC', serif" }}
        >
          <i className="ri-download-line text-sm" />
          下载命理乾坤
        </button>

        {isMobile && (
          <p className="text-[10px] text-center" style={{ color: ink.textSub }}>
            微信内无法直接唤起APP，请点击右上角「在浏览器打开」
          </p>
        )}
      </motion.div>
    </motion.div>
  );
}